import { FunctionComponent, RefObject } from 'react';

import {
  Tooltip,
  useBetSlice,
  useGameStateSlice,
  useDragAndDropOriginConnected,
  useDragAndDropTargetConnected,
  useConfigSlice,
} from '@pp/core';

import { Bets, BetSpotNumber } from '../../../config/bets';
import { BlockReason, useBetBlockedStatusConnected } from '../hooks/useBetBlockedStatusConnected';
import { useBetSpotMultiplierConnected } from '../hooks/useBetSpotMultiplierConnected';
import { useBetSpotValueConnected } from '../hooks/useBetSpotValueConnected';
import { useMegaWheelValueSpotConnected } from '../hooks/useMegaWheelValueSpotConnected';
import { useIsWinningBetSpotConnected } from '../hooks/useIsWinningBetSpotConnected';
import { useOnBetCallbackConnected } from '../hooks/useOnBetCallbackConnected';
import { useMWStateSlice } from '../../../store/slices/mwState/selectors';
import { BetSpot } from './BetSpot';
import { useMegaWinStatus } from '../hooks/useMegaWinStatus';
import { useMWGameGridSlice } from '../../../store/slices/mwGameGrid/selectors';
import { useIsPortraitMax } from '../../../hooks/useWindowDimension';

export interface BetSpotConnectedProps {
  betSpot: Bets;
  betSpotRef?: RefObject<HTMLDivElement>;
  highlight?: boolean;
  noTooltip?: boolean;
  clickSound?: string;
}

export const BetSpotConnected: FunctionComponent<BetSpotConnectedProps> = ({
  betSpot,
  betSpotRef,
  highlight,
  noTooltip,
  clickSound,
}) => {
  const isBetsOpenStatus = useGameStateSlice.isBetsOpenStatus();
  const isLoadedInActiveRound = useGameStateSlice.isLoadedInActiveRound();
  const value = useBetSpotValueConnected(betSpot);
  const megaMultiplier = useMegaWheelValueSpotConnected(betSpot);
  const onBet = useOnBetCallbackConnected();
  const multiplier = useBetSpotMultiplierConnected(betSpot);
  const isWinningBetSpot = useIsWinningBetSpotConnected(betSpot);
  const blockedStatus = useBetBlockedStatusConnected(betSpot);
  const balance = useGameStateSlice.getDeductibleBalance();
  const isLowBalance = useBetSlice.isLowBalance(balance);
  const isBetSpotDisabled = !isBetsOpenStatus || isLowBalance;
  const chipRef = useDragAndDropOriginConnected({ value, betSpot });
  const result = useMWStateSlice.getGameResult();
  const [targetRef, { isDraggedFrom, isDragging }] = useDragAndDropTargetConnected({
    betSpot,
    ref: betSpotRef,
    clickSound,
  });

  const isMegaWin = useMegaWinStatus(betSpot);
  const hoverActiveNumber = useMWGameGridSlice.getHoverActiveNumber();

  const tableBetLimits = useConfigSlice.tableBetLimits()?.max || 1000;
  const totalBet = useBetSlice.totalBet();
  const isPortraitMax = useIsPortraitMax();
  const BetSpotElement = (
    <BetSpot
      value={value}
      bet={betSpot}
      onBet={onBet}
      disabled={isBetSpotDisabled}
      multiplier={multiplier}
      megaMultiplier={megaMultiplier}
      winning={isWinningBetSpot}
      betSpotRef={targetRef as RefObject<HTMLDivElement>}
      chipRef={chipRef as RefObject<HTMLDivElement>}
      isDraggedFrom={isDraggedFrom}
      isDragging={isDragging}
      highlight={highlight}
      isBetsOpenStatus={isBetsOpenStatus}
      isLoadedInActiveRound={isLoadedInActiveRound}
      result={result}
      isMegaWin={isMegaWin}
      clickSound={clickSound}
      isMax={isPortraitMax}
    />
  );
  const [message, attrs] = getMessageAndAtts();

  if (noTooltip) return BetSpotElement;
  const disableTooltip =
    (blockedStatus.reason === BlockReason.LowBalance ? totalBet > 0 : isBetSpotDisabled) ||
    (!blockedStatus.blocked && tableBetLimits !== totalBet);

  return (
    <Tooltip
      text={message}
      attrs={attrs}
      style={{ width: '100%' }}
      disabled={disableTooltip}
      displayOnTouch={true}
      displayTrigger={
        (blockedStatus.blocked || tableBetLimits == totalBet) &&
        hoverActiveNumber == BetSpotNumber[betSpot] &&
        !isLowBalance
      }
    >
      {BetSpotElement}
    </Tooltip>
  );

  function getMessageAndAtts() {
    let message = '';
    let attrs: string[] = [];

    if (blockedStatus.message) {
      message = blockedStatus.message;
      attrs = blockedStatus.attr || [];
    } else if (tableBetLimits == totalBet) {
      message = 'CORE_MAXTABLEBETIS';
      attrs = [tableBetLimits.toString()];
    }
    return [message, attrs] as const;
  }
};
